import { useEffect, useMemo } from 'react';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { useReportError } from '@/hooks/useReportError';
import {
  BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE,
  BENEFICIARIES_SUBFORM,
} from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { defaultBeneficiary } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.defaults';
import { EntityBeneficiariesFormBeneficiary } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.types';
import {
  beneficiaryFragmentToFormBeneficiary,
  sortBeneficiariesByLevel,
} from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.utils';
import {
  gstStatusToFormGSTStatus,
  inEstateStatusToFormEstateInclusionStatus,
} from '@/modules/entities/TaxStatusSubform/TaxStatusSubform.utils';
import {
  TRUST_DETAILS_SUBFORM_NAMESPACE,
  TrustDetails_TrustAdvisor,
} from '@/modules/entities/TrustDetailsSubform/TrustDetailsSubform.types';
import { getNodes } from '@/utils/graphqlUtils';

import {
  CONTROLLING_PARTY_SUBFORM,
  TESTAMENTARY_ENTITY,
} from '../EditTestamentaryEntitySplitScreen.constants';
import { EditTestamentaryEntityFormShape } from '../EditTestamentaryEntitySplitScreen.types';
import { useGetTestamentaryEntityForEditQuery } from '../graphql/GetTestamentaryEntityForEdit.generated';

interface UseEditTestamentaryEntityInitialValuesProps {
  testamentaryEntityId: string;
  onNotFound: () => void;
}

interface UseEditTestamentaryEntityInitialValuesResult {
  initialFormValues: EditTestamentaryEntityFormShape | undefined;
  loading: boolean;
  hasError: boolean;
}

/**
 * Hook to load testamentary entity data and map it to the form shape.
 * Unlike useSyncDataToForm, this hook doesn't sync to an existing form context;
 * instead, it returns the form values to be passed to a FormProvider.
 */
export function useEditTestamentaryEntityInitialValues({
  testamentaryEntityId,
  onNotFound,
}: UseEditTestamentaryEntityInitialValuesProps): UseEditTestamentaryEntityInitialValuesResult {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { recoverMissingRecord } = useRecordNotFoundHandler({
    message: 'This testamentary entity is no longer available.',
    onNotFound,
  });

  const { data, loading, error } = useGetTestamentaryEntityForEditQuery({
    variables: {
      where: {
        id: testamentaryEntityId,
      },
    },
    skip: !testamentaryEntityId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: (err) => {
      showFeedback(FeedbackMessages.queryError);
      reportError('failed to load testamentary entity for edit', err, {
        testamentaryEntityId,
      });
    },
  });

  const initialFormValues = useMemo<
    EditTestamentaryEntityFormShape | undefined
  >(() => {
    if (!data) return undefined;

    const testamentaryEntities = getNodes(data.testamentaryEntities);
    const testamentaryEntity = testamentaryEntities[0];

    if (!testamentaryEntity) {
      return undefined;
    }

    const {
      trustees,
      successorTrustees,
      trustAdvisors,
      displayName,
      kind,
      gstStatus,
      inclusionRatio,
      inEstateStatus,
      survivingSpouseStateInEstateStatus,
      description,
      controllingParties,
      beneficiaries,
      beneficiariesAfterBothGrantorDeaths,
      householdID,
      createdFromEntities,
      accountOwner,
    } = testamentaryEntity;

    const formTrustees = (() => {
      if (trustees?.length) {
        return trustees.map((t) => ({
          trusteeId: (t?.individual?.id || t?.organization?.id) ?? '',
          duties: t?.duties?.map((d) => d.kind) ?? [],
          note: t?.notes ?? '',
        }));
      }

      return [{ trusteeId: '', duties: [], note: '' }];
    })();

    const formSuccessorTrustees = (() => {
      if (successorTrustees?.length) {
        return successorTrustees.map((t) => ({
          successorTrusteeId: (t?.individual?.id || t?.organization?.id) ?? '',
          level: t?.level ?? ('' as const),
          duties: t?.duties?.map((d) => d.kind) ?? [],
          note: t?.notes ?? '',
        }));
      }

      return [
        {
          successorTrusteeId: '',
          level: '' as const,
          duties: [],
          note: '',
        },
      ];
    })();

    const formTrustAdvisors = ((): TrustDetails_TrustAdvisor[] => {
      if (trustAdvisors?.length) {
        return trustAdvisors.map((t) => ({
          trustAdvisorId: (t?.individual?.id || t?.organization?.id) ?? '',
          level: t?.level ?? ('' as const),
          role: t?.roles?.map((r) => r.kind) ?? [],
          note: t?.note ?? '',
        }));
      }

      return [
        {
          trustAdvisorId: '',
          level: '' as const,
          role: [],
          note: '',
        },
      ];
    })();

    const formControllingParties = (() => {
      if (controllingParties?.length) {
        const parties = controllingParties.map((t) => ({
          controllingPartyId: t?.individual?.id ?? '',
        }));
        return {
          [CONTROLLING_PARTY_SUBFORM]: {
            controllingParties: parties,
          },
        };
      }

      return {};
    })();

    const formBeneficiaries: EntityBeneficiariesFormBeneficiary[] = (() => {
      if (!beneficiaries?.length) {
        return [defaultBeneficiary()];
      }

      return sortBeneficiariesByLevel(beneficiaries).map((b) => {
        return beneficiaryFragmentToFormBeneficiary(b);
      });
    })();

    const formBeneficiariesAfterBothDie = (() => {
      const beneficiariesList = sortBeneficiariesByLevel(
        beneficiariesAfterBothGrantorDeaths ?? []
      ).map((b) => {
        return beneficiaryFragmentToFormBeneficiary(b);
      });

      if (!beneficiariesList.length) {
        beneficiariesList.push(defaultBeneficiary());
      }

      return {
        [BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE]: {
          beneficiaries: beneficiariesList,
        },
      };
    })();

    const createdFromEntityIds = getNodes(createdFromEntities).map((e) => e.id);

    const formData: EditTestamentaryEntityFormShape = {
      [TESTAMENTARY_ENTITY]: {
        displayName: displayName,
        householdId: householdID ?? '',
        testamentaryEntityKind: kind,
        gstStatus: gstStatus
          ? gstStatusToFormGSTStatus[gstStatus]
          : ('' as const),
        inclusionRatio: inclusionRatio ?? null,
        estateInclusionStatus: inEstateStatus
          ? inEstateStatusToFormEstateInclusionStatus[inEstateStatus]
          : 'in-estate',
        survivingSpouseStateInEstateStatus: survivingSpouseStateInEstateStatus
          ? inEstateStatusToFormEstateInclusionStatus[
              survivingSpouseStateInEstateStatus
            ]
          : 'in-estate',
        entityDetailsNotes: description ?? '',
        createdFromEntityIds,
        accountOwnerId: accountOwner?.id ?? '',
      },
      [TRUST_DETAILS_SUBFORM_NAMESPACE]: {
        trustees: formTrustees,
        successorTrustees: formSuccessorTrustees,
        trustAdvisors: formTrustAdvisors,
      },
      [BENEFICIARIES_SUBFORM]: {
        beneficiaries: formBeneficiaries,
      },
      ...formBeneficiariesAfterBothDie,
      ...formControllingParties,
    };

    return formData;
  }, [data]);

  useEffect(() => {
    if (data && !loading && !initialFormValues) {
      recoverMissingRecord();
    }
  }, [data, initialFormValues, loading, recoverMissingRecord]);

  return {
    initialFormValues,
    loading,
    hasError: !!error,
  };
}
